/**
 * Canonical list of tracked URL query keys, kept Zod-free so the
 * @markku/attribution IIFE can import this file without dragging in
 * Zod + the rest of @markku/types (drizzle-zod, drizzle-orm, etc.).
 * Ships on every markku.fi / nuohousmarkku.fi pageview — must stay small.
 */

// UTM query keys we persist into `AcquisitionData.utmParams`.
// Adding one: append here + allowlist it in landingPage scrub + extend
// `deriveMedium`/`deriveCampaign` if it needs semantic bucketing.
export const TRACKED_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id', // GA4 campaign-id standard (separate from utm_campaign label)
] as const
export type TrackedUtmKey = (typeof TRACKED_UTM_KEYS)[number]

// Click-ID query keys we lift into top-level fields on `AcquisitionData`.
// Each is set by its ad network's autotagging and uniquely identifies a
// paid click. Add one: append here → add matching field to
// `acquisitionDataSchema` (satisfies Record<TrackedClickIdKey,...> enforces
// this) → add a `deriveSource` branch in @markku/utils/acquisitionSource.
export const TRACKED_CLICK_ID_KEYS = [
  // Google family
  'gclid', //     Google Ads (universal)
  'gclsrc', //    Google Ads source disambiguation (e.g. 'aw.ds' = Display/Search)
  'wbraid', //    Google Ads web-to-app (iOS ATT / SKAdNetwork era)
  'gbraid', //    Google Ads app-to-app (iOS)
  'dclid', //     Display & Video 360 / Campaign Manager
  // Meta
  'fbclid', //    Facebook + Instagram
  // Microsoft
  'msclkid', //   Microsoft Advertising (Bing)
  // Other networks — tracked pre-emptively so future campaigns attribute
  // without a code change; unused fields stay NULL with zero cost.
  'ttclid', //    TikTok Ads
  'twclid', //    X / Twitter Ads
  'li_fat_id', // LinkedIn Ads ("First Advanced Tracking")
  'sccid', //     Snapchat Ads
  'epik', //      Pinterest
  'rdt_cid', //   Reddit Ads
] as const
export type TrackedClickIdKey = (typeof TRACKED_CLICK_ID_KEYS)[number]

// Every query key we ever want to preserve — UTM + click IDs combined.
export const TRACKED_QUERY_KEYS = [
  ...TRACKED_UTM_KEYS,
  ...TRACKED_CLICK_ID_KEYS,
] as const
export type TrackedQueryKey = (typeof TRACKED_QUERY_KEYS)[number]
